import React, { useEffect, useRef, useState } from "react";
import { CardElement } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";

import { VBabyColors } from "../../../../colors";
import {
  FieldError,
  FieldGroup,
  FieldInput,
  FieldLabel,
  IconNote,
} from "../../../../components/vbaby";
import { getCep } from "../../../../lib/api";
import { cepMask, cpfMask } from "../../../../utils/masks";
import { hasLastName } from "../../../../utils/hasLastName";
import { validateCpf } from "../../../../utils/validateCpf";
import { validateEmail } from "../../../../utils/validateEmail";
import {
  FormContainer,
  InlineLinkButton,
  InputsContainer,
  ZipStateRow,
} from "./styles";

const cardElementOptions = {
  style: {
    base: {
      color: VBabyColors.black,
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "18px",

      padding: "10px",
      "::placeholder": {
        color: "black",
      },
      ":focus": {
        backgroundColor: VBabyColors.gray5,
      },
    },
    invalid: {
      color: "red",
      iconColor: "red",
    },
  },
  hidePostalCode: true,
};

const ZIP_LENGTH = 9;

const RESIDENCY_LINK_MARKER = "[link]";

const isFilled = (value) => Boolean(value);

const FIELDS_FILLED_BY_ZIP = ["city", "line1", "state"];

export const Form = ({
  id,
  variant,
  paymentMethod,
  setForm,
  form,
  handleCardChange,
}) => {
  const [errorFields, setErrorFields] = useState([]);
  const { t } = useTranslation();
  const lastQueriedZip = useRef(null);

  const clearFieldError = (field) =>
    setErrorFields((prev) => prev.filter((item) => item !== field));

  const markFieldError = (field) =>
    setErrorFields((prev) => (prev.includes(field) ? prev : [...prev, field]));

  useEffect(() => {
    if (variant !== "address") return;

    if (form?.zip?.length !== ZIP_LENGTH || lastQueriedZip.current === form.zip) {
      return;
    }

    lastQueriedZip.current = form.zip;

    getCep(form.zip)
      .then(({ data }) => {
        if (!data?.localidade && !data?.logradouro && !data?.uf) return;

        setForm((prev) => ({
          ...prev,
          city: data.localidade,
          line1: data.logradouro,
          state: data.uf,
        }));
        setErrorFields((prev) =>
          prev.filter((field) => !FIELDS_FILLED_BY_ZIP.includes(field))
        );
      })
      .catch(() => {});
  }, [form.zip, variant]);

  const renderField = ({
    field,
    label,
    validate,
    message,
    mask,
    errorKey = field,
    hint,
    ...inputProps
  }) => {
    const isInvalid = errorFields.includes(errorKey);
    const inputId = `${id}-${field}`;

    return (
      <FieldGroup>
        <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
        <FieldInput
          id={inputId}
          value={mask ? mask(form[field]) : form[field]}
          aria-invalid={isInvalid}
          onChange={(event) => {
            const value = mask ? mask(event.target.value) : event.target.value;

            setForm((prev) => ({ ...prev, [field]: value }));
            clearFieldError(errorKey);
          }}
          onBlur={(event) => {
            if (validate(event.target.value)) {
              clearFieldError(errorKey);
            } else {
              markFieldError(errorKey);
            }
          }}
          {...inputProps}
        />
        {isInvalid && form[field] && message && (
          <FieldError role="alert">{message}</FieldError>
        )}
        {hint}
      </FieldGroup>
    );
  };

  const isCard = paymentMethod === "card";

  const setBrazilResident = (isBrazilResident) => {
    setForm((prev) => ({
      ...prev,
      isBrazilResident,
      cpf: isBrazilResident ? prev.cpf : "",
    }));
    clearFieldError("CPF");
  };

  const residencyNote = (noteKey, askKey, residencyOnClick) => {
    const linkWord = t("payment.residencyLinkWord");
    const [beforeLink, afterLink = ""] = t(askKey).split(RESIDENCY_LINK_MARKER);

    return (
      <IconNote fontSize="11.5px" fontWeight="400" alignItems="flex-start">
        <FiInfo aria-hidden="true" />
        <span>
          {t(noteKey)}
          {isCard && (
            <>
              {` ${beforeLink}`}
              <InlineLinkButton
                type="button"
                aria-label={t(askKey).replace(RESIDENCY_LINK_MARKER, linkWord)}
                onClick={() => setBrazilResident(residencyOnClick)}
              >
                {linkWord}
              </InlineLinkButton>
              {afterLink}
            </>
          )}
        </span>
      </IconNote>
    );
  };

  const cpfField = renderField({
    field: "cpf",
    errorKey: "CPF",
    label: "CPF",
    maxLength: 14,
    inputMode: "numeric",
    mask: cpfMask,
    validate: validateCpf,
    message: t("payment.invalidCpf"),
    hint: residencyNote(
      "payment.cpfReason",
      "payment.notBrazilResidentAsk",
      false
    ),
  });

  return (
    <FormContainer id={id} data-variant={variant}>

      {variant === "contact" && (
        <InputsContainer>
          {renderField({
            field: "email",
            label: "E-mail",
            type: "email",
            validate: validateEmail,
            message: t("payment.invalidEmail"),
          })}

          {renderField({
            field: "name",
            label: t("payment.name"),
            validate: hasLastName,
            message: t("payment.invalidName"),
          })}

          {isCard && !form.isBrazilResident
            ? residencyNote(
                "",
                "payment.brazilResidentAsk",
                true
              )
            : cpfField}
        </InputsContainer>
      )}

      {variant === "address" && (
        <InputsContainer>
          <ZipStateRow>
            {renderField({
              field: "zip",
              label: "CEP",
              maxLength: 9,
              inputMode: "numeric",
              mask: cepMask,
              validate: (value) => value.length === ZIP_LENGTH,
            })}
            {renderField({
              field: "state",
              label: t("payment.state"),
              maxLength: 2,
              validate: isFilled,
            })}
          </ZipStateRow>

          {renderField({
            field: "city",
            label: t("payment.city"),
            validate: isFilled,
          })}

          {renderField({
            field: "line1",
            label: t("payment.street"),
            validate: isFilled,
          })}
        </InputsContainer>
      )}

      {variant === "card" && (
        <InputsContainer>
          <FieldGroup>
            <FieldLabel as="span">{t("payment.cardData")}</FieldLabel>
            <FieldInput
              as="div"
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <CardElement
                options={cardElementOptions}
                onChange={handleCardChange}
              />
            </FieldInput>
          </FieldGroup>
        </InputsContainer>
      )}
    </FormContainer>
  );
};
